import React, { useState, useCallback } from 'react';
import { useToast } from '../../Utils/toastContext';
import {
  CreditCard,
  ShieldCheck,
  User,
  Calendar,
  Lock,
  Eye,
  EyeOff,
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import WholesaleCalculator from './wholesale-layer';

const PaymentSubsribe = () => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const { showToast } = useToast();
  const [cardName, setCardName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [cardCvv, setCardCvv] = useState('');
  const [cardErrors, setCardErrors] = useState({});
  const [isBillingDifferent, setIsBillingDifferent] = useState(false);
  const [showCvv, setShowCvv] = useState(false);
  const navigate = useNavigate();
  const [billingAddress, setBillingAddress] = useState({
    address: '',
    city: '',
    state: '',
    zip: '',
    country: '',
  });
  const detectCardBrand = (num) => {
    const digits = num.replace(/\D/g, '');
    if (digits.startsWith('4')) return 'visa';
    if (/^5[1-5]/.test(digits)) return 'mastercard';
    if (digits.startsWith('34') || digits.startsWith('37')) return 'amex';
    return 'unknown';
  };
  const plans = [
    {
      title: 'Boutique Agencies',
      subtitle: 'Up to 5 agents',
      price: '$95 / month',
      description:
        'Built for smaller agencies preparing for Tranche 2 AML/CTF compliance.',
      features: [
        'Core AML compliance',
        'Risk assessment & AML program',
        'Training & audit trail',
        'AUSTRAC-aligned reporting',
        '5 included KYC scans',
      ],
      selected: true,
      color: 'emerald',
      action: 'Get Started',
    },
    {
      title: 'Small–Medium Agencies',
      subtitle: '6–25 agents',
      price: '$399 / month',
      description:
        'Designed for agencies needing scalable AML compliance workflows.',
      features: [
        'Core AML compliance',
        'Risk assessment & AML program',
        'Training & audit trail',
        'AUSTRAC-aligned reporting',
        '20 included KYC scans',
      ],
      extras: [
        'Guided onboarding',
        'Full compliance dashboard',
        'Direct customer support',
      ],
      color: 'blue',
      action: 'Contact Us',
    },
    {
      title: 'Growing Agencies',
      subtitle: '26–50 agents',
      price: '$699 / month',
      description:
        'Advanced compliance support for growing agencies managing higher volume.',
      features: [
        'Core AML compliance',
        'Risk assessment & AML program',
        'Training & audit trail',
        'AUSTRAC-aligned reporting',
        '40 included KYC scans',
      ],
      extras: [
        'Advanced analytics',
        'White-glove onboarding',
        'Enterprise-ready workflows',
      ],
      color: 'purple',
      action: 'Contact Us',
    },
    {
      title: 'Enterprise',
      subtitle: '50+ agents',
      price: 'Custom pricing',
      description:
        'Custom compliance solutions for enterprise groups and franchise networks.',
      features: [
        'Core AML compliance',
        'Risk assessment & AML program',
        'Training & audit trail',
        'AUSTRAC-aligned reporting',
        'Custom KYC allowances',
      ],
      extras: [
        'Advanced analytics',
        'White-glove onboarding',
        'Enterprise workflows',
      ],
      color: 'slate',
      action: 'Contact Us',
    },
  ];
  const validateCardFields = () => {
    const errors = {};
    if (!cardName || cardName.trim().length < 2)
      errors.cardName = 'Enter name on card';
    const nums = cardNumber.replace(/\s/g, '');
    if (nums.length < 13 || nums.length > 16)
      errors.cardNumber = 'Enter a valid card number';
    if (!/^\d{2}\/\d{2}$/.test(cardExpiry))
      errors.cardExpiry = 'Use MM/YY format';
    if (!/^\d{3,4}$/.test(cardCvv)) errors.cardCvv = 'Enter 3 or 4 digit CVV';
    setCardErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const formatCardNumber = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 16);
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ');
  };

  const formatExpiry = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 4);
    if (digits.length <= 2) return digits;
    return digits.slice(0, 2) + '/' + digits.slice(2);
  };

  const maskedCardNumber = (num) => {
    const digits = num.replace(/\D/g, '');
    if (!digits) return '•••• •••• •••• ••••';
    const padded = digits.padStart(16, '•');
    return padded.replace(/(\d{4})(?=\d)/g, '$1 ');
  };
  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8 space-y-6">
      {/* SECTION 1 - HEADER */}
      <div className="bg-gradient-to-r from-blue-50 to-white border rounded-2xl p-6">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
          <span className="material-symbols-outlined text-[#375AE7]">
            verified_user
          </span>
          Start your Ratified Journey
        </h1>

        <p className="text-sm text-slate-600 mt-2">Select your features</p>
      </div>

      {/* SECTION 2 - PRODUCT SUMMARY */}
      {/* <div className="bg-white border rounded-2xl p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <span className="material-symbols-outlined text-blue-600">
            inventory_2
          </span>
          You are subscribing to:
        </h2>

        <p className="mt-2 font-medium text-slate-800">
          Ratified Compliance Platform
        </p>

        <ul className="mt-3 space-y-2 text-sm text-slate-600">
          <li className="flex items-start gap-2">
            <span className="text-emerald-500">✔</span>
            Customer onboarding and identity verification
          </li>
          <li className="flex items-start gap-2">
            <span className="text-emerald-500">✔</span>
            AML/CTF compliance management tools
          </li>
          <li className="flex items-start gap-2">
            <span className="text-emerald-500">✔</span>
            Compliance records and monitoring
          </li>
          <li className="flex items-start gap-2">
            <span className="text-emerald-500">✔</span>
            Business and user administration features
          </li>
        </ul>
      </div> */}
      <div className="relative overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-[0_12px_40px_rgba(15,23,42,0.06)]">
        {/* Subtle accent */}
        <div className="pointer-events-none absolute -right-20 -top-20 h-48 w-48 rounded-full bg-blue-500/[0.04] blur-3xl" />

        <div className="relative p-6 sm:p-7">
          {/* Header */}
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
              <span className="material-symbols-outlined text-[21px]">
                verified
              </span>
            </div>

            <div>
              <h2 className="text-xl font-semibold tracking-tight text-slate-950">
                What’s included
              </h2>

              <p className="mt-1.5 text-sm leading-5 text-slate-500">
                Everything you need to get started with verification.
              </p>
            </div>
          </div>

          {/* Pricing entitlement */}
          <div className="mt-6 rounded-2xl border border-blue-100 bg-blue-50/50 p-5">
            <div className="flex items-end justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-blue-600">
                  Monthly access
                </p>

                <div className="mt-1 flex items-baseline gap-1.5">
                  <span className="text-3xl font-semibold tracking-tight text-slate-950">
                    $49
                  </span>

                  <span className="text-sm font-medium text-slate-500">
                    / month
                  </span>
                </div>
              </div>

              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-blue-100">
                <span className="material-symbols-outlined text-[22px] text-blue-600">
                  workspace_premium
                </span>
              </div>
            </div>

            {/* Included allowance */}
            <div className="mt-5 flex items-center gap-3 rounded-xl border border-white/80 bg-white/80 px-4 py-3.5 shadow-sm">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                <span className="material-symbols-outlined text-[18px]">
                  check_circle
                </span>
              </div>

              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900">
                  10 verification certificates
                </p>

                <p className="mt-0.5 text-xs text-slate-500">
                  Included every month with your access fee
                </p>
              </div>
            </div>
          </div>

          {/* Billing information */}
          <div className="mt-5 space-y-3">
            <div className="flex gap-3">
              <span className="material-symbols-outlined mt-0.5 text-[18px] text-slate-400">
                bolt
              </span>

              <p className="text-sm leading-5 text-slate-600">
                Your{' '}
                <span className="font-semibold text-slate-800">
                  $49 access fee
                </span>{' '}
                gives you immediate access to your monthly allowance and is
                charged as soon as you confirm your selection.
              </p>
            </div>

            <div className="flex gap-3">
              <span className="material-symbols-outlined mt-0.5 text-[18px] text-slate-400">
                add_circle
              </span>

              <p className="text-sm leading-5 text-slate-600">
                Need more than your included allowance? Additional verification
                certificates are available as{' '}
                <span className="font-semibold text-slate-800">add-ons</span>{' '}
                and are billed separately on top of your $49 access fee.
              </p>
            </div>
          </div>

          {/* Bottom reassurance */}
          <div className="mt-6 flex items-center gap-2 border-t border-slate-100 pt-5">
            <span className="material-symbols-outlined text-[17px] text-emerald-500">
              check_circle
            </span>

            <span className="text-xs font-medium text-slate-500">
              10 certificates included each month
            </span>
          </div>
        </div>
      </div>
      {/* SECTION 3 - PLAN & PRICING */}
      {/* <div className="mb-6 p-4 rounded-2xl border border-emerald-100 bg-gradient-to-r from-emerald-50 to-blue-50">
        <p className="text-sm font-semibold text-emerald-700">
          Introductory Offer
        </p>
        <p className="text-xs text-slate-600 mt-1">
          Access is provided at no charge until <b>30 June 2026</b>. Paid
          subscriptions commence from <b>1 July 2026</b> unless cancelled prior.
        </p>
      </div> */}
      <WholesaleCalculator />

      {/* SECTION 3 - PLAN & PRICING */}

      {/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {plans.map((plan, idx) => {
          const isSelected = selectedPlan === plan.title;

          return (
            <div
              key={idx}
              onClick={() => setSelectedPlan(plan.title)}
              className={`
    group relative cursor-pointer rounded-3xl overflow-hidden p-6
    transition-all duration-300 hover:-translate-y-1 hover:shadow-xl
    border bg-white

    ${
      isSelected
        ? "border-[#375AE7] ring-2 ring-[#375AE7]/20 shadow-lg"
        : "border-slate-200 hover:border-[#375AE7]/30"
    }
  `}
            >
              <div
                className={`
    absolute top-0 left-0 right-0 h-[4px]
    transition-all duration-300

    ${
      isSelected
        ? "bg-gradient-to-r from-[#375AE7] via-indigo-500 to-cyan-400"
        : "bg-transparent group-hover:bg-gradient-to-r group-hover:from-[#375AE7]/40 group-hover:to-cyan-400/40"
    }
  `}
              />
              {isSelected && (
                <div className="absolute top-4 right-4 bg-[#375AE7] text-white text-[10px] px-3 py-1 rounded-full">
                  Selected
                </div>
              )}
              <h3 className="text-lg font-semibold text-slate-900">
                {plan.title}
              </h3>

              <p className="text-xs text-slate-500 mt-1">{plan.subtitle}</p>
              <div className="mt-2 p-3 rounded-xl bg-slate-50 border border-slate-100">
                <p className="text-xs text-slate-700 leading-relaxed">
                  {plan.description}
                </p>
              </div>
              <div className="mt-5 flex items-end gap-2">
                <p className="text-3xl font-extrabold text-[#375AE7]">
                  {plan.price}
                </p>
              </div>
              <div className="mt-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-[11px] bg-slate-100 text-slate-600">
                  Monthly billing
                </span>
              </div>
              <ul className="mt-5 space-y-2 text-xs text-slate-700">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-emerald-500 mt-0.5">✔</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              {plan.extras && plan.extras.length > 0 && (
                <div className="mt-5 p-4 rounded-2xl border border-indigo-100 bg-gradient-to-r from-indigo-50 to-blue-50 shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-indigo-600 text-sm">✨</span>
                    <p className="text-[11px] font-semibold text-indigo-700 uppercase tracking-wide">
                      Extra Benefits
                    </p>
                  </div>
                  <ul className="space-y-2 text-xs text-slate-700">
                    {plan.extras.map((f, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="text-indigo-500 mt-0.5">＋</span>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </div> */}
      {/* SECTION 4 - PAYMENT DISCLOSURE */}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
        <h3 className="text-sm font-semibold text-amber-700 flex items-center gap-2">
          <span className="material-symbols-outlined">lock</span>
          Payment Timing Disclosure
        </h3>

        <p className="text-sm text-slate-700 mt-2">
          By confirming your selection and entering your card details, you
          authorise Ratified Pty Limited to charge your nominated card
          immediately for your $49 access fee which includes 10 verifications
          per month. Any additional verifications used beyond your monthly
          allowance are billed as add-ons.
        </p>
      </div>

      {/* SECTION 5 - CARD FORM */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mt-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="bg-[#375AE7] bg-opacity-20 text-[#375AE7] p-2 rounded-lg">
              <CreditCard size={18} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800">
                Payment Method
              </h2>
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <ShieldCheck size={14} /> Secure & encrypted
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="col-span-2">
            <label className="text-sm font-medium text-gray-600 mb-1 block">
              Name on card
            </label>
            <div className="relative">
              <User
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                value={cardName}
                onChange={(e) => setCardName(e.target.value)}
                placeholder="Full name as displayed on card"
                className="w-full border border-gray-200 rounded-xl pl-10 pr-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#375AE7]"
              />
            </div>
            {cardErrors.cardName && (
              <p className="text-xs text-red-500 mt-1">{cardErrors.cardName}</p>
            )}
          </div>
          <div className="col-span-2">
            <label className="text-sm font-medium text-gray-600 mb-1 block">
              Card number
            </label>
            <div className="relative">
              <CreditCard
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                inputMode="numeric"
                value={cardNumber}
                onChange={(e) =>
                  setCardNumber(formatCardNumber(e.target.value))
                }
                placeholder="1234 5678 9012 3456"
                maxLength={19}
                className="w-full border border-gray-200 rounded-xl pl-10 pr-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#375AE7]"
              />
            </div>
            {cardErrors.cardNumber && (
              <p className="text-xs text-red-500 mt-1">
                {cardErrors.cardNumber}
              </p>
            )}
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600 mb-1 block">
              Expiry
            </label>
            <div className="relative">
              <Calendar
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                inputMode="numeric"
                value={cardExpiry}
                onChange={(e) => setCardExpiry(formatExpiry(e.target.value))}
                placeholder="MM/YY"
                maxLength={5}
                className="w-full border border-gray-200 rounded-xl pl-10 pr-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#375AE7]"
              />
            </div>
            {cardErrors.cardExpiry && (
              <p className="text-xs text-red-500 mt-1">
                {cardErrors.cardExpiry}
              </p>
            )}
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600 mb-1 block">
              CVV
            </label>

            <div className="relative">
              <Lock
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />

              <input
                type={showCvv ? 'text' : 'password'}
                inputMode="numeric"
                value={cardCvv}
                onChange={(e) =>
                  setCardCvv(e.target.value.replace(/\D/g, '').slice(0, 4))
                }
                placeholder="123"
                maxLength={4}
                className="w-full border border-gray-200 rounded-xl pl-10 pr-10 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <button
                type="button"
                onClick={() => setShowCvv(!showCvv)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showCvv ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>

            {cardErrors.cardCvv && (
              <p className="text-xs text-red-500 mt-1">{cardErrors.cardCvv}</p>
            )}
          </div>
        </div>
        <div className="mt-6">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={isBillingDifferent}
              onChange={() => setIsBillingDifferent(!isBillingDifferent)}
              className="w-4 h-4 confirm-check text-[#375AE7] border-gray-300 rounded focus:ring-[#375AE7]"
            />
            Billing address is different from shipping address
          </label>
        </div>
        {isBillingDifferent && (
          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5 border-t pt-5">
            <div className="col-span-2">
              <label className="text-sm font-medium text-gray-600 mb-1 block">
                Address
              </label>
              <input
                type="text"
                value={billingAddress.address}
                onChange={(e) =>
                  setBillingAddress({
                    ...billingAddress,
                    address: e.target.value,
                  })
                }
                placeholder="Street address"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#375AE7]"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-600 mb-1 block">
                City
              </label>
              <input
                type="text"
                value={billingAddress.city}
                onChange={(e) =>
                  setBillingAddress({
                    ...billingAddress,
                    city: e.target.value,
                  })
                }
                placeholder="City"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#375AE7]"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-600 mb-1 block">
                State
              </label>
              <input
                type="text"
                value={billingAddress.state}
                onChange={(e) =>
                  setBillingAddress({
                    ...billingAddress,
                    state: e.target.value,
                  })
                }
                placeholder="State"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#375AE7]"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600 mb-1 block">
                ZIP Code
              </label>
              <input
                type="text"
                value={billingAddress.zip}
                onChange={(e) =>
                  setBillingAddress({
                    ...billingAddress,
                    zip: e.target.value,
                  })
                }
                placeholder="ZIP Code"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#375AE7]"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600 mb-1 block">
                Country
              </label>
              <input
                type="text"
                value={billingAddress.country}
                onChange={(e) =>
                  setBillingAddress({
                    ...billingAddress,
                    country: e.target.value,
                  })
                }
                placeholder="Country"
                className="w-full c border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#375AE7]"
              />
            </div>
          </div>
        )}
      </div>

      {/* SECTION 6 - CANCELLATION POLICY */}
      <div className="bg-red-50 border border-red-100 rounded-2xl p-6">
        <h3 className="text-sm font-bold text-red-700 mb-2 flex items-center gap-2">
          <span className="material-symbols-outlined">cancel</span>
          Cancellation
        </h3>

        <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">
          <span className="font-semibold">Timing:</span>
          <br />
          Cancellation may be requested at any time and is not limited to a
          specific date within the billing cycle.
          <br />
          <span className="font-semibold"> Effective date:</span>
          <br />
          Cancellation takes effect at the end of the current billing cycle.
          <br />
          <span className="font-semibold">Billing and access:</span>
          <br />
          No further charges will be applied from the following billing cycle.
          No refund, prorated or otherwise, will be issued for the remainder of
          the current cycle. You will retain access to your platform and all
          associated services until the end of the current billing cycle, after
          which your platform access will be terminated.
          <br />
          <span className="font-semibold">
            {' '}
            Wholesale accounts (3,000+ verification checks per month):
          </span>
          <br />
          Requests to downgrade at renewal must be submitted to Ratified at
          least 5 business days prior to the renewal date to allow sufficient
          time for processing.
        </p>
      </div>
      <div className="mt-6 p-4 rounded-xl border border-emerald-100 bg-emerald-50 flex gap-3 items-start">
        {/* Icon */}
        <div className="p-2 rounded-lg bg-emerald-100 text-emerald-600">
          <ShieldCheck size={18} />
        </div>

        {/* Text */}
        <div className="text-sm text-emerald-900 leading-relaxed">
          <p className="font-semibold mb-1">Secure Payment Processing</p>
          <p className="text-emerald-800">
            Payments are processed securely via{' '}
            <span className="font-medium">NAB’s payment gateway</span>. Your
            card details are encrypted and never stored on our servers.
          </p>
        </div>
      </div>
      {/* SECTION 7 - CHECKBOXES */}
      <div className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-2xl p-5 md:p-6 space-y-4 shadow-sm">
        {/* SECTION TITLE */}
        <div className="flex items-center gap-2 mb-2">
          <span className="material-symbols-outlined text-[#375AE7] text-lg">
            verified_user
          </span>
          <h3 className="text-sm md:text-base font-semibold text-slate-800">
            Legal Agreements
          </h3>
        </div>

        {/* CHECKBOX 1 */}
        <label className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 bg-white hover:border-[#375AE7]/40 hover:bg-blue-50/40 transition cursor-pointer">
          <input type="checkbox" className="mt-1 accent-[#375AE7] scale-110" />

          <span className="text-sm text-slate-700 leading-relaxed">
            I agree to the{' '}
            <a
              href="https://ratified.com.au/terms-of-service/"
              target="_blank"
              rel="noreferrer"
              className="text-[#375AE7] font-medium hover:underline"
            >
              Terms and Conditions
            </a>{' '}
            and{' '}
            <a
              href="https://ratified.com.au/privacy-policy/"
              target="_blank"
              rel="noreferrer"
              className="text-[#375AE7] font-medium hover:underline"
            >
              Privacy Policy
            </a>
          </span>
        </label>

        {/* CHECKBOX 2 */}
        <label className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 bg-white hover:border-[#375AE7]/40 hover:bg-blue-50/40 transition cursor-pointer">
          <input type="checkbox" className="mt-1 accent-[#375AE7] scale-110" />

          <span className="text-sm text-slate-700 leading-relaxed">
            I authorise{' '}
            <span className="font-semibold">Ratified Pty Limited</span> to
            charge my card in accordance with the service terms
          </span>
        </label>

        {/* HELP TEXT */}
        <p className="text-xs text-slate-500 px-1">
          You can review these terms at any time before proceeding.
        </p>
      </div>

      {/* SECTION 8 - BUTTON */}
      <div className="flex justify-between items-center mt-6">
        <button
          onClick={(e) => {
            e.preventDefault();

            navigate('/sign-up');
          }}
          className="px-6 py-2.5 rounded-xl bg-[#375AE7] text-white font-medium shadow hover:bg-[#2a48c7] transition flex items-center gap-2"
        >
          <CreditCard size={16} />
          Complete Payment
        </button>
      </div>

      {/* SECTION 9 - FOOTER */}
      <div className="text-xs text-slate-500 text-center pt-6 border-t">
        <p className="font-semibold">Ratified Pty Limited</p>
        <p>
          ABN: 29 688 067 469 | Address: L2, 222 Pitt Street, Sydney NSW 2000
          Australia
        </p>
        <p>Email: admin@ratified.com.au | Phone: (02) 7228 7948</p>
      </div>
    </div>
  );
};

export default PaymentSubsribe;
